import { useCallback } from 'react';

import { type Board } from '@air/api/types';
import { useBreakpointsContext } from '@air/provider-media-query';

import { useEditBoard } from '~/components/EditPrivateBoardModal/hooks/useEditBoard';
import { useDeleteBoardsModal } from '~/components/Modals/DeleteBoards/useDeleteBoardsModal';
import { useDuplicateBoard } from '~/components/Modals/DuplicateBoard/hooks/useDuplicateBoard';
import { useMoveItemsModal } from '~/components/Modals/MoveItemsModal/useMoveItemsModal';
import { type GetItemMenuOptionsCallback } from '~/constants/MenuOptions';
import { usePrivateAddAllBoardAssetsToCanvas } from '~/hooks/canvases/usePrivateAddAllBoardAssetsToCanvas';
import { useGetPrivateBoardMenuOptions } from '~/hooks/getPrivateBoardMenuOptions';
import { useAddToShowcaseOption } from '~/hooks/useAddToShowcaseOption';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useCanShowInFinderOption } from '~/hooks/useCanShowInFinderOption';
import { useCreateSubBoard } from '~/hooks/useCreateSubBoard';
import { useGetPrivateCustomFieldsMenuOptions } from '~/hooks/useGetCustomFieldsMenuOptions';
import { useDesktopAppConnectionContext } from '~/providers/DesktopAppConnectionProvider';
import { useGetBoardContainerPermissions } from '~/swr-hooks/boards/useGetBoardContainerPermissions';
import {
  canChangeBoardCustomFields,
  canChangeBoardDetails,
  canCreateBoard,
  canCreateCanvasInBoard,
  canDeleteBoard,
  canDuplicateBoard,
} from '~/utils/permissions/boardPermissions';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';

export const useGetPrivateBoardMenuOptionsCallback = () => {
  const { showDeleteBoardsModal } = useDeleteBoardsModal();
  const { showDuplicateBoardsModal } = useDuplicateBoard();
  const { showEditBoardModal } = useEditBoard();
  const { showMoveItemsModal } = useMoveItemsModal();
  const { createSubBoard } = useCreateSubBoard();
  const { getCustomFieldsMenuOptions } = useGetPrivateCustomFieldsMenuOptions();
  const getAddToShowcaseOption = useAddToShowcaseOption();
  const { canShowOpenInFinder } = useCanShowInFinderOption();

  const { openBoardInFinder } = useDesktopAppConnectionContext();

  const { getBoardMenuOptions } = useGetPrivateBoardMenuOptions();
  const { addAllBoardAssetsToCanvas } = usePrivateAddAllBoardAssetsToCanvas();
  const { isAboveMediumScreen } = useBreakpointsContext();

  const { getBoardContainerPermissions } = useGetBoardContainerPermissions();
  const { getBoardPermissions } = useBoardPermissionsCache();

  const getBoardMenuOptionsCallback: GetItemMenuOptionsCallback<Board> = useCallback(
    ({ item: board }) =>
      ({ trackLocation }) => {
        const boardContainerPermissions = getBoardContainerPermissions({ board });
        const boardPermissions = getBoardPermissions(board.id);
        const canDuplicate = canDuplicateBoard({
          boardContext: boardPermissions,
          targetContext: boardContainerPermissions,
        });

        if (!boardPermissions || !boardContainerPermissions) {
          return [];
        }

        return getBoardMenuOptions({
          onMove: canDeleteBoard(boardContainerPermissions)
            ? () => showMoveItemsModal({ boards: [board], trackLocation })
            : undefined,
          onEdit: canChangeBoardDetails(boardPermissions)
            ? () => showEditBoardModal({ board, trackLocation })
            : undefined,
          onAddSubBoard: canCreateBoard(boardPermissions)
            ? () => createSubBoard({ selectedParentBoard: board, trackLocation })
            : undefined,
          onDuplicate: canDuplicate ? () => showDuplicateBoardsModal({ boards: [board], trackLocation }) : undefined,
          onDelete: canDeleteBoard(boardContainerPermissions)
            ? () =>
                showDeleteBoardsModal({
                  boardIds: [board.id],
                  trackLocation,
                })
            : undefined,
          customFieldsMenuOptions: canChangeBoardCustomFields(boardPermissions)
            ? getCustomFieldsMenuOptions({ boards: [board] })
            : undefined,
          addToShowcaseOption: getAddToShowcaseOption
            ? getAddToShowcaseOption({ assetIds: [], boardIds: [board.id] })
            : undefined,
          onOpenOnDesktop:
            /**
             * @see https://air-labs-team.slack.com/archives/C052RDYCGAG/p1696874696775379
             */
            canShowOpenInFinder && canGenerateZip(boardPermissions) ? () => openBoardInFinder(board.id) : undefined,
          onAddAllToCanvas:
            isAboveMediumScreen && canCreateCanvasInBoard(boardPermissions)
              ? () => addAllBoardAssetsToCanvas(board)
              : undefined,
        });
      },
    [
      getBoardMenuOptions,
      getBoardPermissions,
      getBoardContainerPermissions,
      getCustomFieldsMenuOptions,
      getAddToShowcaseOption,
      canShowOpenInFinder,
      showMoveItemsModal,
      showEditBoardModal,
      createSubBoard,
      showDuplicateBoardsModal,
      showDeleteBoardsModal,
      openBoardInFinder,
      addAllBoardAssetsToCanvas,
      isAboveMediumScreen,
    ],
  );

  return {
    getBoardMenuOptionsCallback,
  };
};
